import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, MessageCircle } from "lucide-react";

const StarRating = ({ rating, count }) => (
  <div className="flex items-center gap-1.5">
    <div className="flex">
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={`text-sm ${s <= Math.round(rating) ? "star" : "star-empty"}`}>★</span>
      ))}
    </div>
    <span className="text-sm font-medium text-gray-700">{rating > 0 ? rating.toFixed(1) : "New"}</span>
    {count > 0 && <span className="text-xs text-gray-400">({count})</span>}
  </div>
);

export default function RestaurantCard({ restaurant }) {
  const {
    slug, name, description, location, contact, whatsapp,
    logo_url, cover_url, cuisine_type, is_open,
    rating, review_count, min_order, delivery_fee, is_featured,
  } = restaurant;

  const handleCall = (e) => {
    e.preventDefault();
    window.open(`tel:${contact}`);
  };

  const handleWhatsApp = (e) => {
    e.preventDefault();
    const num = whatsapp || contact;
    window.open(`https://wa.me/${num.replace(/\D/g, "")}`);
  };

  return (
    <Link to={`/restaurant/${slug}`} className="block group card-hover">
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
        {/* Cover image */}
        <div className="relative h-44 bg-gradient-to-br from-orange-100 to-amber-100 overflow-hidden">
          {cover_url ? (
            <img
              src={cover_url}
              alt={name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-5xl opacity-30">🍽️</div>
          )}
          {is_featured && (
            <span className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
              ⭐ Featured
            </span>
          )}
          <span
            className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full ${
              is_open ? "bg-green-500 text-white" : "bg-gray-600 text-white"
            }`}
          >
            {is_open ? "Open" : "Closed"}
          </span>
          {/* Logo */}
          {logo_url && (
            <div className="absolute -bottom-6 left-4 w-14 h-14 rounded-xl bg-white shadow-md border-2 border-white overflow-hidden">
              <img src={logo_url} alt={name} className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`p-4 ${logo_url ? "pt-8" : ""}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-gray-900 text-lg leading-tight truncate group-hover:text-orange-600 transition-colors">
                {name}
              </h3>
              {cuisine_type?.length > 0 && (
                <p className="text-xs text-orange-500 font-medium mt-0.5">{cuisine_type.join(" · ")}</p>
              )}
            </div>
          </div>

          {description && (
            <p className="text-sm text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">{description}</p>
          )}

          <div className="mt-3 flex items-center gap-1 text-xs text-gray-500">
            <MapPin className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
            <span className="truncate">{location}</span>
          </div>

          <div className="mt-2">
            <StarRating rating={rating || 0} count={review_count || 0} />
          </div>

          {(min_order > 0 || delivery_fee >= 0) && (
            <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
              {min_order > 0 && <span>Min: ₨{min_order}</span>}
              <span>Delivery: {delivery_fee > 0 ? `₨${delivery_fee}` : "Free"}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleCall}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-orange-600 border border-gray-200 hover:border-orange-300 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Phone className="w-3.5 h-3.5" /> Call
            </button>
            {(whatsapp || contact) && (
              <button
                onClick={handleWhatsApp}
                className="flex items-center gap-1.5 text-xs font-medium text-green-700 hover:text-green-800 border border-green-200 hover:border-green-300 bg-green-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </button>
            )}
            <span className="ml-auto text-xs font-semibold text-orange-600 bg-orange-50 px-3 py-1.5 rounded-lg">
              View Menu →
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
